import React, { useEffect, useRef } from 'react';
import { themeSelectorStyles } from './style';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Tooltip from '@mui/material/Tooltip';
import Input from '@mui/material/Input';
import { SelectChangeEvent } from '@mui/material/Select';
import { useTranslation } from 'react-i18next';
import usePractitionerControlsContext from '../../hooks/usePractitionerControlsContext/usePractitionerControlsContext';
import { Theme } from '../PhaserApp/enums/Themes';

const SPRITE_FRAME_SIZE = 512;

interface ThemeSelectorProps {
  size?: 'small' | 'large';
}

const SpriteIcon: React.FC<{ src: string; size?: number }> = ({ src, size = 20 }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const img = new Image();
    img.src = src;
    img.onload = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      canvas.width = size;
      canvas.height = size;
      ctx.clearRect(0, 0, size, size);
      // draw first frame (0,0, frame)
      ctx.drawImage(img, 0, 0, SPRITE_FRAME_SIZE, SPRITE_FRAME_SIZE, 0, 0, size, size);
    };
  }, [src, size]);

  return <canvas className="icon" ref={canvasRef} width={size} height={size} style={{ width: size, height: size }} />;
};

const themeOptions = [
  { key: 'default', label: 'default', sprite: 'assets/phaser/images/Silver_16.png', value: Theme.Default },
  { key: 'child', label: 'child', sprite: 'assets/phaser/images/rhino_16.png', value: Theme.Child },
];

const ThemeSelector: React.FC<ThemeSelectorProps> = ({ size = 'small' }) => {
  const { t } = useTranslation();
  const { theme, setTheme } = usePractitionerControlsContext();

  const selectedKey: string = theme === Theme.Child ? 'child' : 'default';
  const iconSize = size === 'large' ? 32 : 24;

  const handleSelect = (event: SelectChangeEvent<string>) => {
    const key = event.target.value;
    setTheme(key === 'child' ? Theme.Child : Theme.Default);
  };

  return (
    <Tooltip
      PopperProps={{
        modifiers: [
          {
            name: 'pointerEventsFix',
            enabled: true,
            phase: 'afterWrite',
            fn({ state }) {
              Object.assign(state.elements.popper.style, {
                pointerEvents: 'none',
              });
            },
          },
        ],
      }}
      title={t('theme') as string}
      placement={'left'}
    >
      <FormControl variant="standard" sx={{ minWidth: 'auto' }}>
        <Select
          variant="standard"
          sx={size === 'large' ? themeSelectorStyles.pickerLarge : themeSelectorStyles.picker}
          value={selectedKey}
          onChange={handleSelect}
          input={<Input disableUnderline />}
          renderValue={() => {
            const item = themeOptions.find(o => o.key === selectedKey)!;
            return <SpriteIcon src={item.sprite} size={iconSize} />;
          }}
          MenuProps={{ PaperProps: { sx: themeSelectorStyles.menuPaper } }}
        >
          {themeOptions.map(option => (
            <MenuItem value={option.key} key={option.key}>
              <SpriteIcon src={option.sprite} size={24} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Tooltip>
  );
};

export default ThemeSelector;
